import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  SparklesIcon,
  Loader2Icon,
  DatabaseIcon,
  SettingsIcon,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SoloMockupToggle — Solo dashboard'unda örnek/mockup veri modunu tek tıkla
 * açıp kapatan iOS-tarzı switch.
 *
 * Props:
 *   - variant: 'banner' (default) — bağımsız kart
 *              'inline'           — welcome banner içine gömülü, kompakt
 *   - theme:   'light' (default) — açık arka planda
 *              'dark'             — koyu / gradient arka planda (welcome banner içi)
 */
const SoloMockupToggle = ({ variant = 'banner', theme = 'light' }) => {
  const [isOn, setIsOn] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // dismissed: kullanıcı toggle'ı kalıcı olarak gizlediyse component null döner.
  // Backend'den gelir — clear akışında otomatik set edilir, ayrıca kullanıcı
  // "Bir daha gösterme" bağlantısıyla manuel de tetikleyebilir.
  const [dismissed, setDismissed] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${BACKEND_URL}/api/solo/demo-data/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsOn(!!r.data?.has_demo_data);
      setCount(r.data?.count || 0);
      setDismissed(!!r.data?.dismissed);
    } catch (_e) {
      /* sessizce yut */
    } finally {
      setLoading(false);
    }
  };

  const dismissForever = async () => {
    // Optimistic — hemen gizle, arka planda backend'e yaz.
    setDismissed(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/solo/demo-data/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Örnek veri banner\'ı bir daha gösterilmeyecek');
    } catch (_e) {
      // hata olursa sessizce görünmez bırak — kullanıcı tekrar tetikleyebilir.
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleToggle = async () => {
    if (busy) return;
    const nextState = !isOn;

    if (nextState) {
      setBusy(true);
      try {
        const token = localStorage.getItem('token');
        const r = await axios.post(
          `${BACKEND_URL}/api/solo/demo-data/load`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsOn(true);
        setCount(r.data?.count || 0);
        toast.success(
          r.data?.already_loaded
            ? `Örnek veri zaten yüklü (${r.data.count} kayıt)`
            : `${r.data?.count || '?'} örnek harcama yüklendi 🎉`
        );
        setTimeout(() => window.location.reload(), 900);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Örnek veri yüklenemedi');
      } finally {
        setBusy(false);
      }
    } else {
      const confirmed = window.confirm(
        `${count} örnek harcama + işletmeleri ve projeleri kalıcı olarak silinecek. Sizin manuel eklediğiniz veriler korunur. Devam?`
      );
      if (!confirmed) return;
      setBusy(true);
      try {
        const token = localStorage.getItem('token');
        const r = await axios.post(
          `${BACKEND_URL}/api/solo/demo-data/clear`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const d = r.data?.deleted || {};
        setIsOn(false);
        setCount(0);
        // Clear otomatik olarak dismissed=true set eder — bir sonraki
        // fetch'e gerek yok, direkt gizle.
        setDismissed(true);
        toast.success(
          `Temizlendi: ${d.expenses || 0} harcama, ${d.suppliers || 0} işletme, ${d.projects || 0} proje`
        );
        setTimeout(() => window.location.reload(), 700);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Temizleme başarısız');
      } finally {
        setBusy(false);
      }
    }
  };

  if (loading) return null;
  // Kullanıcı bir kez daha görmek istemedi → hiç render etme.
  if (dismissed) return null;

  // ============================================================
  // INLINE VARIANT — Welcome banner içi kompakt yerleşim
  // ============================================================
  if (variant === 'inline') {
    const isDark = theme === 'dark';
    return (
      <div
        className={`rounded-lg backdrop-blur-sm px-4 py-3 flex items-center gap-3 ${
          isDark ? 'bg-white/15 border border-white/25' : 'bg-white border border-gray-200'
        }`}
        data-testid="solo-mockup-toggle-inline"
      >
        <div
          className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
            isOn
              ? isDark ? 'bg-white/25 text-white' : 'bg-teal-500 text-white'
              : isDark ? 'bg-white/10 text-white/70' : 'bg-gray-200 text-gray-500'
          }`}
        >
          {isOn ? <SparklesIcon className="w-4 h-4" /> : <DatabaseIcon className="w-4 h-4" />}
        </div>

        <div className="min-w-0 mr-1">
          <div className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Örnek Veri Modu
          </div>
          <div className={`text-[11px] ${isDark ? 'text-white/80' : 'text-gray-500'}`}>
            {isOn ? (
              <>
                <strong className={isDark ? 'text-white' : 'text-teal-700'}>{count}</strong> örnek kayıt yüklü
              </>
            ) : (
              'Kapalı · gerçek veri'
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleToggle}
          disabled={busy}
          role="switch"
          aria-checked={isOn}
          aria-label="Örnek veri modu"
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
            isOn
              ? (isDark ? 'bg-white focus:ring-white/60' : 'bg-teal-600 focus:ring-teal-500')
              : (isDark ? 'bg-white/25 focus:ring-white/60' : 'bg-gray-300 focus:ring-teal-500')
          }`}
          data-testid="solo-mockup-toggle-switch"
        >
          <span className="sr-only">Örnek veri modu</span>
          {busy ? (
            <Loader2Icon className={`w-3.5 h-3.5 mx-auto animate-spin ${isDark && isOn ? 'text-teal-700' : 'text-white'}`} />
          ) : (
            <span
              className={`inline-block h-4 w-4 transform rounded-full shadow-md transition-transform ${
                isOn
                  ? `translate-x-6 ${isDark ? 'bg-teal-600' : 'bg-white'}`
                  : `translate-x-1 ${isDark ? 'bg-white' : 'bg-white'}`
              }`}
            />
          )}
        </button>
        {/* Kalıcı gizleme — inline variant'ta küçük × butonu. Sadece toggle
            OFF iken göster (kullanıcı deneyimini bozmayalım). */}
        {!isOn && (
          <button
            type="button"
            onClick={dismissForever}
            aria-label="Bir daha gösterme"
            title="Bir daha gösterme"
            className={`ml-1 w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
              isDark
                ? 'text-white/70 hover:text-white hover:bg-white/15'
                : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
            }`}
            data-testid="solo-mockup-toggle-dismiss-inline"
          >
            <span className="text-sm leading-none">×</span>
          </button>
        )}
      </div>
    );
  }

  // ============================================================
  // BANNER VARIANT (default) — sayfa içi bağımsız kart
  // ============================================================
  return (
    <div
      className={`rounded-xl border-2 p-4 mb-4 transition-colors ${
        isOn
          ? 'border-teal-400 bg-gradient-to-r from-teal-50 to-emerald-50'
          : 'border-gray-200 bg-gray-50'
      }`}
      data-testid="solo-mockup-toggle-banner"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isOn ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}
          >
            {isOn ? <SparklesIcon className="w-5 h-5" /> : <DatabaseIcon className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-gray-900">Örnek Veri Modu (Mockup)</h3>
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                  isOn ? 'bg-teal-600 text-white' : 'bg-gray-300 text-gray-800'
                }`}
                data-testid="solo-mockup-status-pill"
              >
                {isOn ? 'AÇIK' : 'KAPALI'}
              </span>
            </div>
            <p className="text-xs text-gray-600 mt-0.5">
              {isOn ? (
                <>
                  <strong>{count}</strong> örnek harcama yüklü — dashboard&apos;u gerçekmiş gibi denerken görebilirsiniz.
                </>
              ) : (
                <>Kapalı — dashboard sadece kendi girdilerinizi gösteriyor.</>
              )}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleToggle}
          disabled={busy}
          role="switch"
          aria-checked={isOn}
          aria-label="Örnek veri modu"
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
            isOn ? 'bg-teal-600' : 'bg-gray-300'
          }`}
          data-testid="solo-mockup-toggle-switch"
        >
          <span className="sr-only">Örnek veri modu</span>
          {busy ? (
            <Loader2Icon className="w-4 h-4 mx-auto text-white animate-spin" />
          ) : (
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
                isOn ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          )}
        </button>
      </div>

      <div className="mt-2 pt-2 border-t border-gray-200/60 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[11px] text-gray-500">
          {isOn
            ? '🔒 Yalnızca örnek işareti taşıyan kayıtlar temizlenir — kendi verileriniz her zaman korunur.'
            : '💡 Uygulamayı denemek için yandaki düğmeyi açın.'}
        </p>
        <div className="flex items-center gap-3">
          {/* OFF durumdayken sunulan kalıcı gizleme — tıklanınca banner bir
              daha görünmez (Dashboard ve Ayarlar). */}
          {!isOn && (
            <button
              type="button"
              onClick={dismissForever}
              className="text-[11px] text-gray-500 hover:text-red-600 underline underline-offset-2"
              data-testid="solo-mockup-toggle-dismiss-banner"
            >
              Bir daha gösterme
            </button>
          )}
          <a
            href="/dashboard/settings#solo-demo-data"
            className="text-[11px] text-gray-500 hover:text-teal-700 inline-flex items-center gap-0.5"
            data-testid="solo-mockup-settings-link"
          >
            <SettingsIcon className="w-3 h-3" /> Ayarlar
          </a>
        </div>
      </div>
    </div>
  );
};

export default SoloMockupToggle;
